import moment from 'moment'
import { sortObjectArrayByKey, isEmpty } from '../utils/generalUtils'
import {
  isCustomStartInRoute,
  isActiveRouteStop,
  isCustomEndInRoute,
  isTodayOrFuture,
  propertyIdsInRoute,
  isPastRoute,
  getRouteIdsArray,
  getRouteMessagesIdArray,
  totalActiveRouteStops,
  isConfirmedOrScheduledRoute
} from '../utils/routeUtils'
import {
  UpdateScheduleRouteUrl,
  ShareScheduledRouteUrl,
  UpdateDeleteRouteUrl,
  CreateRetrieveRouteUrl,
  CancelConfirmedScheduledRouteUrl
} from '../utils/clientUrls'
import {
  genericPutRequestToApi,
  genericDeleteRequestToApi,
  genericPostRequestToApi
} from '../utils/genericRequests'

const saveRouteRequest = (clientId, postRequest) => {
  return genericPostRequestToApi(
    CreateRetrieveRouteUrl(clientId),
    postRequest,
    { params: { optimize: true } }
  )
}

const requestRouteShowing = data => {
  return genericPutRequestToApi(
    UpdateScheduleRouteUrl(data.clientId, data._id)
  )
}

const sendScheduleRequest = data => {
  return genericPutRequestToApi(
    ShareScheduledRouteUrl(data.clientId, data._id)
  )
}

const updateRouteRequest = (clientId, routeId, updatedValues) => {
  return genericPutRequestToApi(
    UpdateDeleteRouteUrl(clientId, routeId),
    updatedValues,
    { params: { optimize: true } }
  )
}

const deleteRouteRequest = (clientId, routeId) => {
  return genericDeleteRequestToApi(UpdateDeleteRouteUrl(clientId, routeId))
}

const cancelConfirmedScheduledRouteRequest = (clientId, routeId) =>
  genericPutRequestToApi(CancelConfirmedScheduledRouteUrl(clientId, routeId))

const activeRouteHomeIds = clientData => {
  return clientData && clientData.routes
    ? propertyIdsInRoute(clientData.routes).propertyIds
    : []
}

const updateSavedRoutesOnPendingRequest = (clientData, routeData) => {
  const { updateCounter, deleteCounter } = removeHomesFromSaved(
    clientData,
    routeData
  )

  let message = `Request have been send to respective listing agents.`
  message += updateCounter > 0 ? 'Also Updated ' + updateCounter : ''
  message += deleteCounter > 0 ? 'and deleted ' + deleteCounter : ''
  if (updateCounter > 0 || deleteCounter > 0) {
    message += `saved route(s) that have this route's home present in them.`
  }
  return message
}

const updateRouteDataOnResponse = (updatedRoute, routeList) => {
  const routes = routeList.map(route =>
    route._id === updatedRoute._id
      ? { ...updatedRoute, ...createCalendarTimeFrame(updatedRoute) }
      : route
  )
  return sortObjectArrayByKey(routes, 'startTime', 'desc')
}

const createCalendarTimeFrame = route => {
  const routeTime = moment(route.startTime)
  const timeDiff = calendarEventGenerator(route, routeTime)
  const timeRangeParams = {
    date: routeTime.format('YYYY-MM-DD'),
    time: routeTime.format('HH:mm'),
    timeDiff,
    open: false
  }
  return { ...route, ...timeRangeParams }
}

const addLastMessageHourDiff = (routeList, messagesList) => {
  const routeWithMessages = getLatestRouteMessages(routeList, messagesList)
  const msgRouteIds = getRouteMessagesIdArray(routeWithMessages)
  const currentTime = moment.utc().format()
  return routeList.map(route => {
    if (msgRouteIds.includes(route._id) && isActiveScheduledRoute(route)) {
      const msgIndex = msgRouteIds.indexOf(route._id)
      const msgTime = routeWithMessages[msgIndex]._timestamp
      const duration = moment.duration(
        moment(currentTime).diff(moment(msgTime))
      )
      const hourDifference = parseInt(duration.asHours())
      return { ...route, lastMsgDuration: hourDifference }
    }
    return route
  })
}

const getLatestRouteMessages = (routeList, messagesList) => {
  return getExistingRouteMsg(routeList, messagesList).filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        t =>
          t.metadata.data.thread.route_id === item.metadata.data.thread.route_id
      )
  )
}

const getExistingRouteMsg = (routeList, messagesList) => {
  const idsArray = getRouteIdsArray(routeList)
  return getRouteScheduleMessages(messagesList).filter(
    msg =>
      msg.metadata.data && idsArray.includes(msg.metadata.data.thread.route_id)
  )
}

const getRouteScheduleMessages = messagesList => {
  return messagesList.filter(
    msg => msg.metadata && msg.metadata.message_type === 'route_schedule'
  )
}

/**
 * Checks for properties in saved routes and update by filtering them out
 * that are present in the routeData that is set to pending
 *
 * @param {Object} clientData - client to which route belong
 * @param {Object} routeData - route set to pending
 */
const removeHomesFromSaved = (clientData, routeData) => {
  const routePropertyIds = propertyIdsInRoute(clientData.routes).propertyIds
  let updateCounter = 0
  let deleteCounter = 0

  const routesToCheck = clientData.routes.filter(
    route => route._id !== routeData._id
  )

  routesToCheck.forEach(async route => {
    const routeStops = route.routeStops
    if (isSavedRoute(route) && isTodayOrFuture(route.startTime)) {
      const totalStops = routeStops.length
      let updatedStops = routeStops.filter(
        stop => !routePropertyIds.includes(stop.propertyId)
      )
      const putRequest = { ...route, routeStops: updatedStops }
      if (updatedStops.length < totalStops) {
        if (updatedStops.length > 0) {
          await updateRouteRequest(clientData._id, putRequest._id, putRequest)
          updateCounter++
        } else {
          await deleteRouteRequest(clientData._id, putRequest._id)
          deleteCounter++
        }
      }
    }
  })
  return { updateCounter, deleteCounter }
}

const isSavedRoute = route => {
  return !isEmpty(route.routeStops) && route.state === 'saved'
}

const isActiveScheduledRoute = route => {
  return route.state === 'scheduled' && !isPastRoute(route)
}

const calendarEventGenerator = (route, routeTime) => {
  let timeFrom = null
  let timeTo = null
  let timeDiff = null
  if (route.routeStops && totalActiveRouteStops(route.routeStops) > 1) {
    let routeStops = [...route.routeStops]
    routeStops = sortObjectArrayByKey(routeStops, 'arrivalTime')
    route = { ...route, routeStops }
    if (isCustomStartInRoute(route)) timeFrom = moment(route.startTime)
    else {
      for (let j = 0; j < routeStops.length; j++) {
        const stop = routeStops[j]
        if (isActiveRouteStop(stop)) {
          timeFrom = moment(stop.arrivalTime)
          break
        }
      }
    }

    for (let k = routeStops.length - 1; k >= 0; k--) {
      const stop = routeStops[k]
      if (isActiveRouteStop(stop) && stop.arrivalTime) {
        timeTo = moment(stop.arrivalTime)
        break
      }
    }
    if (isCustomEndInRoute(route)) timeTo = moment(timeTo).add(1800, 'seconds')
    if (!timeTo) timeDiff = '60'
    else {
      timeDiff = timeTo.diff(
        timeFrom < routeTime ? timeFrom : routeTime,
        'minutes'
      )
    }
  } else if (route.routeStops.length > 0) {
    timeFrom = moment(route.routeStops[0].arrivalTime).format('HH:mm')
    timeDiff = '60'
  }
  return timeDiff
}

// Function to delete/cancel a route
const cancelDeleteRoute = async (route) => {
  const isSaved = !isConfirmedOrScheduledRoute(route)
  const response = !isSaved
    ? await cancelConfirmedScheduledRouteRequest(route.clientId, route._id)
    : await deleteRouteRequest(route.clientId, route._id)

  if (response.status === 200) {
    return { isSaved, data: isSaved ? route : response.data }
  }
  return null
}

export {
  saveRouteRequest,
  requestRouteShowing,
  sendScheduleRequest,
  updateRouteRequest,
  deleteRouteRequest,
  cancelConfirmedScheduledRouteRequest,
  activeRouteHomeIds,
  updateSavedRoutesOnPendingRequest,
  updateRouteDataOnResponse,
  createCalendarTimeFrame,
  addLastMessageHourDiff,
  isActiveScheduledRoute,
  calendarEventGenerator,
  cancelDeleteRoute
}
